import { RouterOutput, ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import {
  isErrorWithCode,
  isErrorWithMessage,
  isErrorWithStatus,
} from "api/helpers";
import {
  useGetQuestionnaireContentQuery,
  useQuestionnaireTableData,
} from "api/useQuestionnaireQueries";
import { AssignIcbtModal } from "components/icbt/assignIcbtModal";
import { LastIcbtActivity } from "components/lib/LastIcbtActivity";
import { mergeContentWithAnswers } from "components/questionnaires/questionnaireModal/mergeContentWithAnswers";
import { Typography } from "components/typography";
import CONSTANTS from "core/constants";
import { usePsychologistUser } from "hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Link } from "components/link";
import { useMindlerApi } from "api/apiv2";
import { Slot } from "../../../../core/types";
import { useCloseDialog } from "../actions";
import { SelectActionMenu } from "./selectActionMenu";
import { PatientCard } from "./slotPatientCard";
import { MedicoreId } from "components/patients/patientCard/patientData/medicore/MedicoreId.tsx";
import { useAuth } from "@mindlercare/auth-ui";
import { countries } from "@mindlercare/schema";

export const SlotPatientData = ({
  slot,
  patientUserIdFromSearchParams,
  handleGetPatientData,
}: {
  slot: Slot;
  patientUserIdFromSearchParams: string | null;
  handleGetPatientData: (
    patient: RouterOutput["patients"]["getByUserId"],
  ) => Promise<void>;
}) => {
  const { market } = useAuth();
  const { t } = useTranslation();
  const userPsychologistId = usePsychologistUser().psychologist?.psychologistId;
  const closeWithHistory = useCloseDialog(true);

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(slot.userId || patientUserIdFromSearchParams),
      },
      {
        enabled: !!(slot.userId || patientUserIdFromSearchParams),
      },
    ),
  );

  useEffect(() => {
    if (patientUserIdFromSearchParams && patient && !slot.userId) {
      handleGetPatientData(patient);
    }
  }, [
    handleGetPatientData,
    patient,
    patientUserIdFromSearchParams,
    slot.userId,
  ]);

  const PHQ9_CLINICAL_ID = "PHQ-9";
  const { data: questionnaireContent, isFetching: contentPending } =
    useGetQuestionnaireContentQuery(PHQ9_CLINICAL_ID);
  const {
    data: patientQuestionnaireAnswersAndRequests,
    isLoading: answersPending,
    isError,
    errors,
  } = useQuestionnaireTableData({
    patientUserId: patient?.userId,
  });

  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [phq9Score, setPhq9Score] = useState<string | undefined>(undefined);
  const [showLastScoreLink, setShowLastScoreLink] = useState(true);

  useEffect(() => {
    const noAccess =
      isErrorWithCode(errors?.userAnswerError, 403) ||
      isErrorWithStatus(errors?.userAnswerError, 403);

    if (noAccess) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowLastScoreLink(false);
      if (isErrorWithMessage(errors?.userAnswerError, "Not Authorized")) {
        setPhq9Score(t("NOT_PATIENT_QUESTIONNAIRES_ERROR"));
      } else {
        setPhq9Score(t("NOT_CONSENTED_QUESTIONNAIRES_ERROR"));
      }
      return;
    }

    if (isError) {
      setShowLastScoreLink(false);
      setPhq9Score(t("FAILED_FETCH"));
      return;
    }
    if (contentPending || answersPending) {
      setPhq9Score(t("FETCHING"));
      return;
    }
    const finishedQuestionnaires =
      patientQuestionnaireAnswersAndRequests.filter(
        (q) => q.clinicalId === PHQ9_CLINICAL_ID && q.dateCompleted !== "--",
      );
    if (finishedQuestionnaires.length === 0) {
      setShowLastScoreLink(false);
      setPhq9Score("--");
      return;
    }
    const latestFinishedQuestionnaire = finishedQuestionnaires.reduce(
      (max, obj) =>
        (obj.dateCompleted as string) > (max.dateCompleted as string)
          ? obj
          : max,
    );

    const relevantRequestId = latestFinishedQuestionnaire?.userAnswer
      ? latestFinishedQuestionnaire?.userAnswer[0]?.requestId
      : undefined;

    if (!relevantRequestId) {
      setShowLastScoreLink(false);
      setPhq9Score(t("FAILED_FETCH"));
      return;
    }

    setRequestId(relevantRequestId);

    const questionnaireMergedWithAnswers = mergeContentWithAnswers(
      relevantRequestId,
      questionnaireContent,
      latestFinishedQuestionnaire?.userAnswer,
    );

    if (
      !questionnaireMergedWithAnswers?.questions ||
      questionnaireMergedWithAnswers?.questions?.length < 9
    ) {
      setShowLastScoreLink(false);
      setPhq9Score(t("FAILED_FETCH"));
      return;
    }
    // since this is a clinical questionnaire the order of questions is fixed
    // and we can safely pick question with index 8
    const riskQuestion = questionnaireMergedWithAnswers?.questions[8];
    if (riskQuestion?.userAnswer?.score == null) {
      setShowLastScoreLink(false);
      setPhq9Score(t("FAILED_FETCH"));
      return;
    }
    setShowLastScoreLink(true);
    setPhq9Score(`${riskQuestion.userAnswer.score}/3`);
  }, [
    answersPending,
    contentPending,
    patientQuestionnaireAnswersAndRequests,
    questionnaireContent,
    t,
    errors?.userAnswerError,
    isError,
  ]);

  const showPatientCard =
    patient?.userId && slot.status !== CONSTANTS.BOOKING_STATUS.OPEN;
  const [assignIcbtModalIsOpen, setAssignIcbtModalIsOpen] = useState(false);

  return (
    <>
      {assignIcbtModalIsOpen && patient ? (
        <AssignIcbtModal
          patient={patient}
          closeModal={() => setAssignIcbtModalIsOpen(false)}
        />
      ) : null}
      <PatientTitleContainer>
        {patient && (
          <>
            <PatientNameTypography variant="body">
              {`${patient?.firstName} ${patient?.lastName}`}
            </PatientNameTypography>
            <SelectActionMenu
              slot={slot}
              patient={patient}
              onOpenAssignModal={() => setAssignIcbtModalIsOpen(true)}
              onClose={() => closeWithHistory()}
            />
          </>
        )}
      </PatientTitleContainer>
      <PatientDataContainer>
        <MedicoreId patientUserId={patient?.userId} />
        {market === countries.SE.codeISO2 && (
          <PatientDataItem data-testid={"national-identity"}>
            <LabelTypography variant="body-x-small">
              {t("NATIONAL_IDENTITY")}
            </LabelTypography>
            <ValueTypography variant="body-small">
              {patient?.nationalIdentity ?? t("NO_PATIENT_YET")}
            </ValueTypography>
          </PatientDataItem>
        )}
        <PatientDataItem data-testid={"phone-number"}>
          <LabelTypography variant="body-x-small" insensitive>
            {t("PHONE_NUMBER")}
          </LabelTypography>
          <ValueTypography variant="body-small">
            {patient?.phoneNumber ?? t("NO_PATIENT_YET")}
          </ValueTypography>
        </PatientDataItem>
        <PatientDataItem data-testid={"user-id"}>
          <LabelTypography variant="body-x-small" insensitive>
            {t("USER_ID")}
          </LabelTypography>
          <ValueTypography variant="body-small">
            {patient?.userId ?? t("NO_PATIENT_YET")}
          </ValueTypography>
        </PatientDataItem>
      </PatientDataContainer>
      <PatientDataContainer>
        <PatientDataItem data-testid={"patient-card"}>
          <LabelTypography variant="body-x-small" insensitive>
            {t("PATIENT_CARD")}
          </LabelTypography>
          {showPatientCard ? (
            <PatientCard
              selectedPsychologistId={userPsychologistId}
              patientUserId={patient.userId}
              onClose={closeWithHistory}
            />
          ) : (
            <ValueTypography variant="body-small">
              {t("NO_PATIENT_YET")}
            </ValueTypography>
          )}
        </PatientDataItem>
        <PatientDataItem data-testid={"last-icbt-activity"}>
          <LabelTypography variant="body-x-small" insensitive>
            {t("LATEST_ICBT_ACTIVITY")}
          </LabelTypography>
          {patient?.patientId ? (
            <LastIcbtActivity
              onClickLink={closeWithHistory}
              patientUserId={patient.userId}
              selectedPsychologistId={userPsychologistId}
            />
          ) : (
            <ValueTypography variant="body-small">
              {t("NO_PATIENT_YET")}
            </ValueTypography>
          )}
        </PatientDataItem>
        <PatientDataItem>
          <LabelTypography variant="body-x-small" insensitive>
            {t("PHQ9_RISK_QUESTION")}
          </LabelTypography>
          {patient?.patientId ? (
            <>
              {showLastScoreLink && (
                <Link
                  onClick={closeWithHistory}
                  to={`/questionnaires/${patient.userId}`}
                  variant="body"
                  state={{ clinicalId: PHQ9_CLINICAL_ID, requestId }}
                >
                  {t("LATEST_SCORE")}
                </Link>
              )}
              <ValueTypography variant="body-small">
                {phq9Score}
              </ValueTypography>
            </>
          ) : (
            <ValueTypography variant="body-small">
              {t("NO_PATIENT_YET")}
            </ValueTypography>
          )}
        </PatientDataItem>
      </PatientDataContainer>
    </>
  );
};

const PatientTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 24px;
`;

const PatientNameTypography = styled(Typography)`
  margin: 0;
  font-size: ${({ theme }) => theme.FONT.SIZE.EXTRA_LARGE};
`;

const PatientDataContainer = styled.div`
  display: grid;
  grid-template-columns: 224px 200px 200px;
  column-gap: 16px;
  overflow: hidden;
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL};

  @media (max-width: ${({ theme }) => theme.BREAKPOINTS.MD}) {
    grid-template-columns: auto;
  }
`;

const PatientDataItem = styled.div`
  flex-direction: column;
  display: flex;
  padding-bottom: 24px;
`;

const LabelTypography = styled(Typography)`
  margin-bottom: 10px;
  color: ${({ theme }) => theme.COLORS.GRAY_DARK};
`;

const ValueTypography = styled(Typography)`
  word-break: break-word;
  margin: 0;
`;
